import { useRef } from 'react';
import PropTypes from 'prop-types';
import {
    FormControl,
    FormHelperText,
    FormLabel,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { trakrefBlue, red, selectedButtonWhite, Grey2 } from '../../../color_utils';
import { trakrefFontFamily } from '../../../utils';


const BinaryButtonGroup = ({
    id,
    label,
    value,
    options,
    onChange,
    required,
    error,
    helperText,
    disabled,
    sx,
}) => {
    const selectedValue = value === true || value === false ? value : null;
    const labelId = id ? `${id}-label` : 'binary-button-group-label';
    const buttonRefs = useRef([]);

    const handleChange = (event, newValue) => {
        if (event.defaultPrevented) return;
        if (newValue === null) return;
        onChange(newValue);
    };

    const handleKeyDown = (event, index) => {
        const nextKeys = ['ArrowRight', 'ArrowDown'];
        const previousKeys = ['ArrowLeft', 'ArrowUp'];

        if (![...nextKeys, ...previousKeys].includes(event.key)) return;

        event.preventDefault();
        const offset = nextKeys.includes(event.key) ? 1 : -1;
        const nextIndex = (index + offset + options.length) % options.length;
        buttonRefs.current[nextIndex]?.focus();
    };

    return (
        <FormControl
            id={id}
            required={required}
            error={error}
            disabled={disabled}
            sx={{
                width: '100%',
                ...sx,
            }}
        >
            <FormLabel
                id={labelId}
                required={required}
                sx={{
                    color: error ? red : Grey2,
                    fontFamily: trakrefFontFamily,
                    fontSize: '14px',
                    lineHeight: '20px',
                    marginBottom: '14px',
                    '&.Mui-focused': { color: error ? red : Grey2 },
                    '&.Mui-error': { color: `${red} !important` },
                    '& .MuiFormLabel-asterisk': { color: red },
                }}
            >
                {label}
            </FormLabel>
            <ToggleButtonGroup
                exclusive
                aria-labelledby={labelId}
                value={selectedValue}
                onChange={handleChange}
                disabled={disabled}
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    '& .MuiToggleButtonGroup-grouped': {
                        border: '1px solid #dddddd',
                        borderRadius: '4px !important',
                        margin: 0,
                    },
                }}
            >
                {options.map((option, index) => (
                    <ToggleButton
                        disableFocusRipple
                        disableRipple
                        key={`${id}-${option.value}`}
                        ref={element => {
                            buttonRefs.current[index] = element;
                        }}
                        value={option.value}
                        onKeyDown={event => handleKeyDown(event, index)}
                        sx={{
                            minWidth: '80px',
                            minHeight: '44px',
                            padding: '10px 24px',
                            color: Grey2,
                            fontFamily: trakrefFontFamily,
                            fontSize: '14px',
                            lineHeight: '20px',
                            textTransform: 'none',
                            '&.Mui-selected': {
                                backgroundColor: option.selectedBackgroundColor || selectedButtonWhite,
                                borderColor: option.selectedColor || trakrefBlue,
                                borderWidth: '2px',
                                color: option.selectedColor || trakrefBlue,
                                '&:hover': {
                                    backgroundColor: option.selectedBackgroundColor || selectedButtonWhite,
                                },
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                            '&.Mui-focusVisible': {
                                outline: `2px solid ${trakrefBlue}`,
                                outlineOffset: '2px',
                            },
                        }}
                    >
                        {option.label}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {error && helperText && (
                <FormHelperText
                    sx={{
                        fontFamily: trakrefFontFamily,
                        marginLeft: 0,
                    }}
                >
                    {helperText}
                </FormHelperText>
            )}
        </FormControl>
    );
};

BinaryButtonGroup.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.bool.isRequired,
        label: PropTypes.string.isRequired,
        selectedColor: PropTypes.string,
        selectedBackgroundColor: PropTypes.string,
    })).isRequired,
    onChange: PropTypes.func.isRequired,
    required: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    sx: PropTypes.object,
};

BinaryButtonGroup.defaultProps = {
    value: null,
    required: false,
    error: false,
    helperText: 'This field is required',
    disabled: false,
    id: '',
    sx: {},
};

export default BinaryButtonGroup;
